import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteContext } from "../contexts/SiteProvider";
import { ADMIN_USER_LOGO, LOGO } from "../services/ImageService";
import ToggleSwitch from "./ToggleSwitch";

interface HeaderProps {
  setIsOpen: (open: boolean) => void;
  setIsDark: (open: boolean) => void;
  isOpen: boolean;
  isDark: boolean;
}

const PAGE_TITLES: Record<string, string> = {
  "/e-fuel/dashboard":           "Dashboard",
  "/e-fuel/offices-list":        "Offices",
  "/e-fuel/hubs-list":           "Hubs",
  "/e-fuel/customers":           "Customers",
  "/e-fuel/vendors-rates-list":  "Hub vs Customers",
  "/e-fuel/vehicles-report":     "Vehicles Report",
  "/e-fuel/consumption-report":  "Consumption Report",
  "/e-fuel/invoices":            "Invoices",
  "/e-fuel/meter-reading-report":"Meter Reading",
  "/e-fuel/msi-reports":         "MIS Reports",
  "/e-fuel/cameras":             "Camera",
  "/e-fuel/payment":             "Payment",
  "/e-fuel/users":               "Users",
  "/e-fuel/settings":            "Settings",
  "/e-fuel/roles-list":          "Roles",
  "/e-fuel/modual-admin":        "Module Admin",
  "/e-fuel/modual-admin-access": "Module Access",
  "/e-fuel/profile":             "Profile",
};

const EFHeader: React.FC<HeaderProps> = ({ setIsOpen, isOpen, setIsDark, isDark }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useSiteContext();

  const [mobileScreen, setMobileScreen] = useState<boolean>(window.innerWidth <= 768);

  const toggleTheme = () => setIsDark(!isDark);

  const pageTitle = PAGE_TITLES[location.pathname] ?? "Electric Fuel";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    const handleResize = () => setMobileScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) setIsOpen(false);
  }, [mobileScreen]);

  return (
    <nav
      className="navbar is-transparent efl-main-nav"
      role="navigation"
      aria-label="main navigation"
    >
      {/* ── Mobile brand bar ── */}
      <div className="navbar-brand">
        <div className="header-brand-logo is-hidden-desktop">
          <img src={LOGO} alt="Brand Logo" />
        </div>
        <a
          role="button"
          className="navbar-burger"
          aria-label="menu"
          aria-expanded="false"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>

      {/* ── Desktop nav ── */}
      <div id="navbarBasicExample" className="navbar-menu">
        {/* Left – hamburger + page title */}
        <div className="navbar-start is-align-items-center">
          <a
            onClick={() => setIsOpen(!isOpen)}
            className="navbar-item efl-nav-hamburger"
          >
            <i className="fa fa-bars"></i>
          </a>
          <div className="navbar-item efl-nav-title is-hidden-touch">
            {/* {pageTitle} */}
          </div>
        </div>

        {/* Right – toggle + user profile */}
        <div className="navbar-end is-align-items-center">
          <div className="dark-switch-item mt-4 mr-2">
            <ToggleSwitch isDark={isDark} toggleTheme={toggleTheme} />
          </div>

          <div className="navbar-item has-dropdown is-hoverable">
            <a className="navbar-link">
              <img
                src={ADMIN_USER_LOGO}
                alt="user"
                className="efl-user-avatar"
              />
              <span className="efl-user-name ml-2 is-hidden-touch">
                {user?.ename}
              </span>
            </a>

            <div className="navbar-dropdown is-right">
              <a
                className="navbar-item"
                onClick={() => navigate("/e-fuel/profile")}
              >
                <span className="icon mr-1">
                  <i className="fa fa-user-circle-o"></i>
                </span>
                Profile
              </a>
              <hr className="navbar-divider" />
              <a onClick={handleLogout} className="navbar-item">
                <span className="icon mr-1">
                  <i className="fa fa-sign-out"></i>
                </span>
                Logout
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default EFHeader;
